import React from "react";
import { PublicAmorgUser, PermissionGroups, AmorgUserRole } from "../../../amorg-common";
import { ButtonRow, BorderRow } from "../../../designSystem/button/ButtonRow";
import { colors } from "../../../designSystem/colors/color";
import { H2, H3 } from "../../../designSystem/typography/Heading";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { IconSize } from "../../../designSystem/icons/Icons";
import { Avatar } from "../../../designSystem/avatar/Avatar";
import { TextAvatar, TextAvatarSizes } from '../../../designSystem/avatar/TextAvatar';
import { IconAvatar } from "../../../designSystem/avatar/IconAvatar";
import { AvatarCard, FullUserAvatarCard } from "../../../designSystem/avatar/AvatarCard";
import { UserAvatar } from "../../../designSystem/avatar/UserAvatar";
import { Small } from "../../../designSystem/typography/Body";
import { CountAvatar } from "../../../designSystem/avatar/CountAvatar";

export const AvatarOverview = (): JSX.Element =>
{
    const [selectedAvatars, setSelectedAvatars] = React.useState<string[]>([]);

    const sampleAvatarUsers: PublicAmorgUser[] = [
        new PublicAmorgUser(
            'user-anne', 'Anne', '', 'Berg', 'AB', 'anne@example.com', true,
            new AmorgUserRole('role-hr', 'user-anne', 'hr', 'HR Manager', new Date(2020, 1, 1), new Date(2030, 1, 1), true),
            PermissionGroups.member,
            { articleContact: false },
            new Date(2020, 1, 1)
        ),
        new PublicAmorgUser(
            'user-bo', 'Bo', 'von', 'Christensen', 'BvC', 'bo@example.com', true,
            new AmorgUserRole('role-safety', 'user-bo', 'safety', 'Arbejdsmiljørepræsentant', new Date(2021, 1, 1), new Date(2031, 1, 1), true),
            PermissionGroups.employee,
            { articleContact: true },
            new Date(2021, 1, 1)
        ),
    ];

    function handleSelectAvatar(size:string)
    {
        if (selectedAvatars.includes(size))
            setSelectedAvatars(selectedAvatars.filter(s => s !== size));
        else
            setSelectedAvatars([...selectedAvatars, size]);
    }

    return (
        <Flex flexDirection="column" gap={16}>
            <ButtonRow variant={"left-aligned"}>
                <H2>Avatar</H2>
            </ButtonRow>

            <BorderRow>
                <H3>AvatarCard</H3>
                <Flex gap={8} alignItems="center" flexWrap="wrap">
                    <AvatarCard
                        avatarText="AB"
                        primaryText="Default"
                        secondaryText="anne@example.com"
                    />
                    <AvatarCard
                        avatarText="BC"
                        avatarColor="green"
                        primaryText="Custom color"
                        secondaryText="Arbejdsmiljørepræsentant"
                    />
                    <AvatarCard
                        avatarText="?"
                        avatarColor={colors.avatar.backgroundMuted}
                        primaryText="No secondary text"
                        secondaryText=""
                    />
                    <AvatarCard
                        avatarText="X1"
                        primaryText="No border"
                        secondaryText=""
                        noBorder
                    />
                    <AvatarCard
                        avatarText="X2"
                        primaryText="With onClick"
                        secondaryText="click this"
                        onClick={() => alert('click!')}
                    />
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>AvatarUserCard</H3>
                <Flex gap={8} alignItems="center" flexWrap="wrap">
                    <FullUserAvatarCard user={sampleAvatarUsers[0]} secondaryTextType="email" />
                    <FullUserAvatarCard user={sampleAvatarUsers[0]} secondaryTextType="role" />
                    <FullUserAvatarCard user={sampleAvatarUsers[0]} secondaryTextType="custom" secondaryText="Custom text" />
                    <FullUserAvatarCard user={sampleAvatarUsers[1]} secondaryTextType="email" />
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>AvatarUserCard without user</H3>
                <Flex gap={8} alignItems="center" flexWrap="wrap">
                    <FullUserAvatarCard />
                    <FullUserAvatarCard noUserText="Vælg en deltager" />
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>UserAvatar</H3>
                <Flex gap={8} alignItems="center" flexWrap="wrap">
                    <UserAvatar user={{ displayName: 'Anne Berg', initials: 'AB' }} />
                    <UserAvatar user={{ displayName: 'Anne Berg', initials: 'AB' }} selected />
                    <UserAvatar user={{ displayName: 'Bo von Christensen', initials: 'BvC' }}
                        backgroundColorOverride={colors.avatar.backgroundMuted} />
                    <UserAvatar user={{ displayName: 'Anne Berg', initials: 'AB' }} size="24px" />
                    <UserAvatar user={{ displayName: 'Anne Berg', initials: 'AB' }} size="48px" />
                    <UserAvatar user={{ displayName: 'Anne Berg', initials: 'AB' }}
                        extraTooltipRows={['HR Manager']} />
                    <UserAvatar />
                    <UserAvatar noUserTooltip={<span>Vælg en deltager</span>} />
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>CountAvatar</H3>
                <Flex gap={8} alignItems="center">
                    {[TextAvatarSizes[0], TextAvatarSizes[2], TextAvatarSizes[4]].map((size) =>
                        [-1, 0, 1, 2].map((count) =>
                            <CountAvatar key={`${size}-${count}`} size={size} count={count}  />
                        )
                    )}
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>TextAvatar</H3>
                <Flex gap={8} alignItems="center">
                    {TextAvatarSizes.map((a) =>
                        <TextAvatar key={a} size={a}
                            text={a.substring(0, 2) || 'N/A'} 
                        />
                    )}
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>IconAvatar</H3>
                <Flex gap={8} alignItems="center">
                    {[ [undefined,undefined], ['32px','small'], ['48px','medium'], ['72px','large']].map((a, index) =>
                        <Flex flexDirection="column" alignItems="center" key={index.toString()}>
                            <Small>{a[0] || 'undefined'} / {a[1] || 'undefined'}</Small>
                            <IconAvatar size={a[0]} iconSize={a[1] as IconSize}
                                icon="actions_unavailable"
                            />
                        </Flex>
                    )}
                </Flex>
            </BorderRow>

            <BorderRow>
                <H3>Avatar</H3>
                <Flex gap={8} alignItems="center">
                    {TextAvatarSizes.map((a) =>
                        <Avatar key={a} size={a}>
                            {a.substring(0, 2) || 'N/A'}
                        </Avatar>
                    )}
                </Flex>

                <H3>Avatar with selected</H3>
                <Flex gap={8} alignItems="center">
                    {TextAvatarSizes.map((a,i) =>
                        <Avatar key={a} size={a}
                            selected={i%2===0}>

                            {a.substring(0, 2) || 'N/A'}
                        </Avatar>
                    )}
                </Flex>

                <H3>Avatar with onClick and tooltip</H3>
                <Flex gap={8} alignItems="center">
                    {TextAvatarSizes.map((a) =>
                        <Avatar key={a} size={a} colorGenerationString={a}
                            selected={selectedAvatars.includes(a)} 
                            identifier={a} 
                            toolTip={<span>Avatar size: {a}</span>}
                            onClick={handleSelectAvatar}>
                            {a.substring(0, 2) || 'N/A'}
                        </Avatar>
                    )}
                </Flex>

                <H3>Avatar with onClick and tooltip and custom border</H3>
                <Flex gap={8} alignItems="center">
                    {TextAvatarSizes.map((a) =>
                        <Avatar key={a} size={a} colorGenerationString={a}
                            selected={selectedAvatars.includes(a)} 
                            identifier={a} 
                            onClick={handleSelectAvatar}
                            borderColor="yellow">

                            {a.substring(0, 2) || 'N/A'}
                        </Avatar>
                    )}
                </Flex>
            </BorderRow>
        </Flex>
    );
};
